import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mergeActivity, tasksFor } from "@foreman/app-core";
import type { OfficeActivity, OfficeAgent, OfficeMessage, OfficeSummary, OfficeTask } from "@foreman/protocol";
import { Buddy } from "./Buddy";
import { MOOD, PILL, kickerOf, lookOf, refsIn, type Desk, type Mood } from "./look";
import { clock, providerLabel } from "../team-ui";
import type { ChatEntry, PlanStep } from "./chat";
import { Spinner } from "../ui";

type Conn = ReturnType<typeof import("../ui").useConnection>["conn"];

export function Pill({ desk }: { desk: Pick<Desk, "state" | "label"> }) {
  const [bg, fg] = PILL[desk.state];
  return (
    <span className="tw-state" style={{ background: bg, color: fg, marginTop: 0 }}>
      {desk.label}
    </span>
  );
}

/** The sheet frame: dimmed backdrop, a handle, content that slides up. Escape and a tap outside close it. */
export function Sheet({ open, hand, onClose, label, children }: { open: boolean; hand?: boolean; onClose: () => void; label: string; children: React.ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const on = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [open, onClose]);
  return (
    <>
      <div className={"tw-back" + (open ? " on" : "")} onClick={onClose} aria-hidden />
      <div className={"tw-sheet" + (open ? " on" : "") + (hand ? " hand" : "")} role="dialog" aria-modal={open} aria-label={label} aria-hidden={!open}>
        <div className="tw-grab"><i /></div>
        {open && children}
      </div>
    </>
  );
}

// ---- agent ------------------------------------------------------------------------------------------------------

type Tab = "activity" | "terminal" | "changes" | "steps";

export function AgentSheet({ conn, agent, desk, tasks, live, ready, computerName, onClose, toast }: { conn: Conn; agent: OfficeAgent; desk: Desk; tasks: OfficeTask[]; live: OfficeActivity[] | undefined; ready: boolean; computerName: string; onClose: () => void; toast: (m: string) => void }) {
  const look = lookOf(agent);
  const [tab, setTab] = useState<Tab>("activity");
  const [summary, setSummary] = useState<OfficeSummary | null>(null);
  const steps = tasksFor(tasks, agent.id);
  const current = steps.find((t) => t.status === "doing") ?? steps[0];
  const lastPrompt = (live ?? []).find((e) => e.kind === "prompt");
  const assignment = current?.title ?? lastPrompt?.title ?? desk.task;

  useEffect(() => {
    if (!ready || !conn) return;
    let stop = false;
    const load = () => void conn.officeSummary(agent.id).then((s) => !stop && s && setSummary(s));
    load();
    const t = setInterval(load, 15_000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [ready, conn, agent.id]);

  const files = summary?.filesEdited ?? [];
  const tabs: { id: Tab; label: string; n: string }[] = [
    { id: "activity", label: "Activity", n: summary?.total ? String(summary.total) : "" },
    { id: "terminal", label: "Terminal", n: "" },
    { id: "changes", label: "Changes", n: files.length ? String(files.length) : "" },
    { id: "steps", label: "Steps", n: steps.length ? String(steps.length) : "" },
  ];

  return (
    <div className="tw-agent" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div className="tw-sh-head">
        <Buddy color={look.color} shape={look.shape} acc={look.acc} size={56} mood={MOOD[desk.state]} />
        <div className="main">
          <div className="tw-sh-name">
            <b>{agent.name}</b>
            <span>{providerLabel(agent.provider)}</span>
          </div>
          <div className="tw-sh-pill"><Pill desk={desk} /></div>
        </div>
        <button className="tw-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="tw-assign">
        <div className="tw-label">ASSIGNMENT</div>
        <p>{assignment}</p>
      </div>
      <Controls conn={conn} agent={agent} ready={ready} toast={toast} />
      <div className="tw-scroll">
        {desk.state === "wait" && (
          <div className="tw-needs">
            <div className="tw-label" style={{ color: "#e5a53a", letterSpacing: ".04em", fontSize: 12 }}>NEEDS YOU · {providerLabel(agent.provider).toUpperCase()} · {computerName.toUpperCase()}</div>
            <p>{agent.name} is waiting for an answer. Open its terminal to see the question and reply.</p>
            <button className="tw-cta" style={{ margin: 0, height: 42 }} onClick={() => setTab("terminal")}>Open terminal</button>
          </div>
        )}
        <div className="tw-tabs" role="tablist" aria-label={`${agent.name} views`}>
          {tabs.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} className={"tw-tab" + (tab === t.id ? " on" : "")} onClick={() => setTab(t.id)}>
              {t.label}
              {t.n && <small>{t.n}</small>}
            </button>
          ))}
        </div>
        <div className="tw-pane">
          {tab === "activity" && <Activity conn={conn} agentId={agent.id} live={live} ready={ready} />}
          {tab === "terminal" && <Terminal conn={conn} agent={agent} ready={ready} toast={toast} />}
          {tab === "changes" &&
            (files.length === 0 ? (
              <div className="tw-empty">Nothing here yet.</div>
            ) : (
              <>
                <div className="tw-files">
                  {files.map((f) => (
                    <div key={f} title={f}><bdi>{f}</bdi></div>
                  ))}
                </div>
                <div className="tw-hint">Files {agent.name} has edited.</div>
              </>
            ))}
          {tab === "steps" &&
            (steps.length === 0 ? (
              <div className="tw-empty">Nothing here yet.</div>
            ) : (
              <ol className="tw-steps" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {steps.map((t, i) => (
                  <li key={t.id} className={"tw-step" + (t.status === "done" ? " done" : "")}>
                    <span className="n">{i + 1}</span>
                    <span className="m">
                      <div>{t.title}</div>
                      <small>{t.status}</small>
                    </span>
                  </li>
                ))}
              </ol>
            ))}
        </div>
      </div>
    </div>
  );
}

function Controls({ conn, agent, ready, toast }: { conn: Conn; agent: OfficeAgent; ready: boolean; toast: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [steer, setSteer] = useState(false);
  const [note, setNote] = useState("");
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  const run = async (action: "pause" | "resume" | "halt" | "steer", text?: string, done?: string) => {
    if (!conn || busy) return false;
    setBusy(true);
    const r = await conn.officeControl(agent.id, action, text);
    setBusy(false);
    if (r.ok) {
      if (done) toast(done);
      conn.refreshOffice();
    } else toast(r.message ?? "Couldn't do that");
    return r.ok;
  };
  const paused = agent.onHold;
  return (
    <>
      <div className="tw-acts">
        <button className="tw-act" disabled={!ready || busy} onClick={() => void run(paused ? "resume" : "pause", undefined, paused ? `${agent.name} resumed` : `${agent.name} paused`)}>
          {paused ? "Resume" : "Pause"}
        </button>
        <button className="tw-act" disabled={!ready} aria-expanded={steer} onClick={() => setSteer((v) => !v)}>
          Steer
        </button>
        <button
          className="tw-act stop"
          disabled={!ready || busy}
          onClick={() => {
            if (!armed) return setArmed(true);
            setArmed(false);
            void run("halt", undefined, `${agent.name} stopped`);
          }}
        >
          {armed ? "Tap again to stop" : "Stop"}
        </button>
      </div>
      {steer && (
        <form
          className="tw-steer"
          onSubmit={(e) => {
            e.preventDefault();
            const text = note.trim();
            if (!text) return;
            void run("steer", text, `Told ${agent.name}`).then((ok) => ok && (setNote(""), setSteer(false)));
          }}
        >
          <input className="tw-input" value={note} maxLength={4000} placeholder={`Tell ${agent.name} what to do differently`} aria-label={`Steer ${agent.name}`} onChange={(e) => setNote(e.target.value)} />
          <button className="tw-send" type="submit" disabled={!note.trim() || busy} aria-label="Send">↑</button>
        </form>
      )}
    </>
  );
}

function Activity({ conn, agentId, live, ready }: { conn: Conn; agentId: string; live: OfficeActivity[] | undefined; ready: boolean }) {
  const [older, setOlder] = useState<OfficeActivity[]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const first = useCallback(async () => {
    if (!conn) return;
    setLoading(true);
    const r = await conn.officeHistory({ agentId, limit: 50 });
    setLoading(false);
    setFailed(!r.ok);
    if (r.ok) {
      setOlder(r.entries);
      setCursor(r.nextBefore);
    }
  }, [conn, agentId]);
  useEffect(() => {
    if (ready) void first();
  }, [ready, first]);

  const more = async () => {
    if (!conn || !cursor || loading) return;
    setLoading(true);
    const r = await conn.officeHistory({ agentId, before: cursor, limit: 50 });
    setLoading(false);
    if (!r.ok) return setFailed(true);
    setOlder((o) => mergeActivity(o, r.entries));
    setCursor(r.nextBefore);
  };
  const entries = useMemo(() => mergeActivity(live ?? [], older), [live, older]);

  if (failed && entries.length === 0)
    return (
      <div className="tw-empty">
        Couldn't read the history. <button className="tw-more-btn" onClick={() => void first()}>Try again</button>
      </div>
    );
  if (entries.length === 0) return loading || cursor === undefined ? <div className="tw-live"><Spinner size={12} />Loading…</div> : <div className="tw-empty">Nothing here yet.</div>;
  return (
    <>
      <div className="tw-log">
        {entries.map((e) => {
          const expandable = !!(e.detail || e.file);
          const isOpen = !!open[e.id];
          return (
            <button key={e.id} className={`tw-log-row k-${e.kind}`} disabled={!expandable} aria-expanded={expandable ? isOpen : undefined} onClick={() => setOpen((o) => ({ ...o, [e.id]: !o[e.id] }))}>
              <span className="tw-log-t">{clock(e.ts)}</span>
              <span className="tw-log-x">
                <span>{e.title}</span>
                {isOpen && e.file && <code>{e.file}</code>}
                {isOpen && e.detail && <code>{e.detail}</code>}
              </span>
            </button>
          );
        })}
      </div>
      {cursor && (
        <button className="tw-more-btn" disabled={loading} onClick={() => void more()}>
          {loading && <Spinner size={12} />}Load earlier
        </button>
      )}
      {!cursor && cursor !== undefined && <div className="tw-hint" style={{ textAlign: "center", marginTop: 18 }}>That's everything recorded so far.</div>}
    </>
  );
}

function Terminal({ conn, agent, ready, toast }: { conn: Conn; agent: OfficeAgent; ready: boolean; toast: (m: string) => void }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [line, setLine] = useState("");
  const [sending, setSending] = useState(false);
  const box = useRef<HTMLPreElement>(null);
  const stick = useRef(true);
  useEffect(() => {
    if (!ready || !conn) return;
    let stop = false;
    const read = async () => {
      const r = await conn.officeTerminal(agent.id);
      if (stop) return;
      if (r.ok) {
        setText(r.text);
        setError(null);
      } else setError(r.message ?? "No terminal for this agent.");
    };
    void read();
    const t = setInterval(() => void read(), 2000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [ready, conn, agent.id]);
  useEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [text]);
  const send = async () => {
    const v = line.trim();
    if (!v || !conn || sending) return;
    setSending(true);
    const r = await conn.officeInput(agent.id, v, true);
    setSending(false);
    if (r.ok) setLine("");
    else toast(r.message ?? "Couldn't type that");
  };
  return (
    <>
      {error && text === null ? (
        <div className="tw-empty">{error} It only has one while it is running.</div>
      ) : (
        <>
          <div className="tw-live"><i className="tw-dotg" />live session</div>
          <pre
            ref={box}
            className="tw-term"
            tabIndex={0}
            aria-label={`${agent.name}'s terminal`}
            onScroll={(e) => {
              const el = e.currentTarget;
              stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
            }}
          >
            {text === null ? "Loading…" : text.trim() ? text : "Nothing on screen."}
          </pre>
        </>
      )}
      <form
        className="tw-steer"
        style={{ padding: "12px 0 0" }}
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input className="tw-input" value={line} maxLength={4000} placeholder={`Type to ${agent.name}'s terminal`} aria-label={`Type to ${agent.name}'s terminal`} onChange={(e) => setLine(e.target.value)} />
        <button className="tw-send" type="submit" disabled={!line.trim() || sending || !!error} aria-label="Send">↑</button>
      </form>
    </>
  );
}

// ---- hand-over --------------------------------------------------------------------------------------------------

export function HandoverSheet({ message, from, to, onClose, onOpen }: { message: OfficeMessage; from?: OfficeAgent; to?: OfficeAgent; onClose: () => void; onOpen: (agentId: string) => void }) {
  const fromLook = from ? lookOf(from) : null;
  const toLook = to ? lookOf(to) : null;
  const title = message.subject.trim() || message.body.trim().split("\n")[0]!.slice(0, 120);
  const refs = refsIn(`${message.subject}\n${message.body}`);
  return (
    <div className="tw-ho" aria-label="Hand-over">
      <button className="tw-x" onClick={onClose} aria-label="Close" style={{ alignSelf: "flex-end", marginBottom: -30 }}>×</button>
      <div className="tw-ho-pair">
        <div className="who">
          {fromLook ? <Buddy color={fromLook.color} shape={fromLook.shape} acc={fromLook.acc} size={56} mood="happy" /> : <Buddy color="oklch(0.7 0.02 282)" shape="50%" size={56} mood="idle" />}
          <span>{from?.name ?? message.from}</span>
        </div>
        <div className="link"><i /><span className="tw-env"><em /><em /></span><i /></div>
        <div className="who">
          {toLook ? <Buddy color={toLook.color} shape={toLook.shape} acc={toLook.acc} size={56} mood="work" /> : <Buddy color="oklch(0.7 0.02 282)" shape="50%" size={56} mood="idle" />}
          <span>{to?.name ?? message.to}</span>
        </div>
      </div>
      <div className="tw-label">{kickerOf(message.act)} · {clock(message.ts)}</div>
      <h3>{title}</h3>
      {message.body.trim() && message.body.trim() !== title && <p>{message.body.trim()}</p>}
      {refs.length > 0 && <div className="tw-refs">{refs.map((r) => <span key={r}>{r}</span>)}</div>}
      <div style={{ flex: 1 }} />
      {to && <button className="tw-cta" onClick={() => onOpen(to.id)}>Open {to.name}</button>}
    </div>
  );
}


/** Dot writes in markdown; show it as text people can read (bold, code, bullet lists), never as raw asterisks. No HTML is ever injected. */
function inline(line: string, k: string): React.ReactNode[] {
  return line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? <strong key={k + i}>{part.slice(2, -2)}</strong>
    : part.startsWith("`") && part.endsWith("`") && part.length > 2 ? <code key={k + i}>{part.slice(1, -1)}</code>
    : part);
}
function Rich({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/).map((b) => b.split("\n"));
  return (
    <>
      {blocks.map((lines, bi) =>
        lines.every((l) => /^\s*([-*•]|\d+[.)])\s+/.test(l)) ? (
          <ul key={bi}>{lines.map((l, i) => <li key={i}>{inline(l.replace(/^\s*([-*•]|\d+[.)])\s+/, ""), `${bi}-${i}-`)}</li>)}</ul>
        ) : (
          <p key={bi}>{lines.map((l, i) => <span key={i}>{i > 0 && <br />}{inline(l.replace(/^#{1,4}\s+/, ""), `${bi}-${i}-`)}</span>)}</p>
        ),
      )}
    </>
  );
}

// ---- the coordinator's chat -------------------------------------------------------------------------------------

const GLYPH: Record<string, string> = { done: "✓", doing: "●", blocked: "◷", todo: "○" };

export interface PlanCard {
  title: string;
  steps: PlanStep[];
}

export function ChatSheet({ lead, workers, mood, entries, typing, plan, summary, nameOf, lookFor, onOpenHandover, onSend, onClose, suggestions, prefill, onChangePlan }: {
  lead: OfficeAgent | undefined;
  workers: OfficeAgent[];
  mood: Mood;
  entries: ChatEntry[];
  typing: boolean;
  plan: PlanCard | null;
  summary: { done: number; total: number; files: string[] } | null;
  nameOf: (id: string) => string;
  lookFor: (id: string) => { color: string; shape: string };
  onOpenHandover: (m: OfficeMessage) => void;
  onSend: (text: string) => Promise<boolean>;
  onClose: () => void;
  suggestions: string[];
  /** Put this text in the box (and focus it): "Change plan". */
  prefill: { text: string; n: number } | null;
  onChangePlan: () => void;
}) {
  const leadLook = lead ? lookOf(lead) : { color: "oklch(0.8 0.12 295)", shape: "50%", acc: "sprout" as const };
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [entries.length, typing, plan?.steps.map((s) => s.status).join(",")]);
  useEffect(() => {
    if (!prefill) return;
    setDraft(prefill.text);
    box.current?.focus({ preventScroll: true });
  }, [prefill?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = lead?.name ?? "Dot";
  const empty = entries.length === 0;
  const submit = async (text: string) => {
    const v = text.trim();
    if (!v || sending) return;
    setDraft(""); // the bubble is already in the conversation; the box is free for the next thing
    setSending(true);
    const ok = await onSend(v);
    setSending(false);
    if (!ok) setDraft(v); // it did not go: give the words back
  };
  const done = plan ? plan.steps.filter((s) => s.status === "done").length : 0;
  const running = plan ? plan.steps.some((s) => s.status === "doing") : false;
  const [planOpen, setPlanOpen] = useState(false);
  const busy = typing || running;
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }} aria-label={`Chat with ${name}`}>
      <div className="tw-chat-head">
        <Buddy color={leadLook.color} shape={leadLook.shape} acc={leadLook.acc} size={40} mood={mood} />
        <div className="m">
          <b>{name}</b>
          <small><i className={busy ? "on" : ""} />{busy ? "working" : "ready"} · coordinator{workers.length ? ` · ${workers.map((w) => w.name).join(", ")}` : ""}</small>
        </div>
        <button className="tw-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      {plan && (
        <div className={"tw-pbar" + (planOpen ? " open" : "")}>
          <button onClick={() => setPlanOpen((o) => !o)} aria-expanded={planOpen}>
            <span className="k">PLAN · {done} / {plan.steps.length}</span>
            <span className="t">{plan.title}</span>
            <span className="bar"><i style={{ width: `${plan.steps.length ? (done / plan.steps.length) * 100 : 0}%` }} /></span>
            <span className="chev" aria-hidden>▾</span>
          </button>
          {planOpen && (<div className="tw-plan" data-testid="chat-plan">
          <div className="k">PLAN · {done === plan.steps.length ? "ALL DONE" : `${done} OF ${plan.steps.length} DONE`}</div>
          <h4>{plan.title}</h4>
          <ol>
            {plan.steps.map((s) => {
              const l = s.owner ? lookOf(s.owner) : null;
              return (
                <li key={s.id} className={s.status}>
                  <span className="n">{s.n}</span>
                  <span className="m">
                    <div>{s.title}</div>
                    <small>
                      {s.owner ? `${s.owner.name} · ${providerLabel(s.owner.provider)}` : "Unassigned"}
                      {s.after.length > 0 ? ` · after ${s.after.join(", ")}` : ""}
                      {s.parallel && <span className="par">parallel</span>}
                    </small>
                  </span>
                  {l && s.owner && <Buddy color={l.color} shape={l.shape} acc={l.acc} size={28} mood={s.status === "done" ? "done" : s.status === "doing" ? "work" : "idle"} />}
                  <span className="st" aria-label={s.status}>{GLYPH[s.status] ?? "○"}</span>
                </li>
              );
            })}
          </ol>
          <div className="tw-plan-foot">
            <span>{done === plan.steps.length ? "All done" : running ? `Running · ${done} of ${plan.steps.length} done` : "Waiting to start"}</span>
            <button onClick={onChangePlan}>Change plan</button>
          </div>
        </div>)}
        </div>
      )}
      <div className="tw-chat" ref={list} role="log" aria-live="polite">
        {empty && (
          <div className="tw-hello">
            <h3>What should {name} get done?</h3>
            <p>Say it in plain words. {name} splits it into steps and brings in the agents it needs.</p>
            {suggestions.map((s) => (
              <button key={s} className="tw-suggest" onClick={() => { setDraft(s); box.current?.focus({ preventScroll: true }); }}>{s}</button>
            ))}
          </div>
        )}
        {entries.map((m) =>
          m.kind === "you" ? (
            <div key={m.id} className="tw-turn me"><div className="who">YOU · {clock(m.ts)}</div><div className="body">{m.text}</div></div>
          ) : m.kind === "dot" ? (
            <div key={m.id} className="tw-turn lead"><div className="who"><b>{name.toUpperCase()}</b> · {clock(m.ts)}</div><div className="body"><Rich text={m.text} /></div></div>
          ) : m.kind === "plan" ? null : (
            <button key={m.id} className="tw-event" onClick={() => onOpenHandover(m.message)}>
              <i className="sw" style={{ background: lookFor(m.from).color, borderRadius: lookFor(m.from).shape }} />
              <span className="tx"><b>{nameOf(m.from)} → {nameOf(m.to)}</b> · {m.message.subject.trim() || m.message.body.trim().split("\n")[0]}</span>
              <span className="v">View</span>
            </button>
          ),
        )}
        {typing && <div className="tw-turn lead"><div className="who"><b>{name.toUpperCase()}</b></div><div className="tw-typing" aria-label={`${name} is typing`}><i /><i /><i /></div></div>}
        {summary && (
          <div className="tw-card">
            <div className="k">MISSION COMPLETE</div>
            <h4>{summary.done} of {summary.total} steps done</h4>
            {summary.files.length > 0 && <ul>{summary.files.slice(0, 6).map((f) => <li key={f}>{f}</li>)}</ul>}
            <div className="note">Open an agent to see what it changed.</div>
          </div>
        )}
      </div>
      <form
        className="tw-compose"
        onSubmit={(e) => {
          e.preventDefault();
          void submit(draft);
        }}
      >
        <input ref={box} className="tw-input" value={draft} maxLength={8000} placeholder={`Message ${name}…`} aria-label={`Message ${name}`} enterKeyHint="send" autoComplete="off" autoCapitalize="sentences" onChange={(e) => setDraft(e.target.value)} />
        <button className="tw-send" type="submit" disabled={!draft.trim() || sending} aria-label="Send">{sending ? <Spinner size={14} /> : "↑"}</button>
      </form>
    </div>
  );
}
